import { api } from "../lib/api.js";

// The §3.5 naming catalog — the "wrangler secret list" of Clawnify. One read
// returns the union of static canon and this org's live state so callers reuse
// canonical identifiers instead of inventing them.
interface Catalog {
  integrations: Array<{
    id: string;
    label: string;
    connected: boolean;
    kind: string;
    accessor: string;
    description?: string;
    hasTriggers?: boolean;
  }>;
  providerKeys: Array<{ name: string; provider: string; set: boolean; accessor: string }>;
  customEnv: Array<{ name: string; set: boolean; hint?: string }>;
}

export async function connectionsListCommand(opts?: { json?: boolean }): Promise<void> {
  const res = await api.get("/v1/connections/catalog");
  if (!res.ok) {
    console.error("Failed to fetch the connections catalog.");
    process.exit(1);
  }

  const catalog = (await res.json()) as Catalog;

  // Agent-consumable form: the full catalog payload, including fields the
  // table omits (docs, spec_url — the provider's published OpenAPI spec).
  if (opts?.json) {
    console.log(JSON.stringify(catalog, null, 2));
    return;
  }

  const idWidth = Math.max(2, ...catalog.integrations.map((i) => i.id.length));
  const labelWidth = Math.max(5, ...catalog.integrations.map((i) => i.label.length));

  console.log("Integrations (use the exact id — never invent a slug)\n");
  console.log(
    `  ${"Id".padEnd(idWidth)}  ${"Label".padEnd(labelWidth)}  ${"State".padEnd(9)}  Accessor`
  );
  console.log(
    `  ${"─".repeat(idWidth)}  ${"─".repeat(labelWidth)}  ${"─".repeat(9)}  ${"─".repeat(30)}`
  );
  for (const i of catalog.integrations) {
    const state = i.connected ? "● active" : "○ —";
    console.log(
      `  ${i.id.padEnd(idWidth)}  ${i.label.padEnd(labelWidth)}  ${state.padEnd(9)}  ${i.accessor}`
    );
  }

  const keyWidth = Math.max(4, ...catalog.providerKeys.map((k) => k.name.length));
  console.log("\nProvider keys (fixed names, set in Settings → API Keys)\n");
  for (const k of catalog.providerKeys) {
    console.log(`  ${k.name.padEnd(keyWidth)}  ${k.set ? "● set" : "○ unset"}`);
  }

  console.log("\nCustom env (this org's user-defined vars)\n");
  if (catalog.customEnv.length === 0) {
    console.log("  (none set)");
  } else {
    const envWidth = Math.max(4, ...catalog.customEnv.map((e) => e.name.length));
    for (const e of catalog.customEnv) {
      console.log(`  ${e.name.padEnd(envWidth)}  ● set${e.hint ? `  (${e.hint})` : ""}`);
    }
  }
}

const INTEGRATIONS_URL = "https://app.clawnify.com/settings/integrations";

/**
 * `clawnify connections connect <id>` — explain how to connect an integration.
 * Connecting is an OAuth flow that needs the user's authorization in a browser;
 * neither the CLI nor an agent can do it. This validates the id against the
 * catalog and prints the canonical steps (so the FDE relays correct guidance
 * instead of improvising a dashboard path) — it never attempts the connect.
 */
export async function connectionsConnectCommand(
  id: string,
  opts?: { json?: boolean },
): Promise<void> {
  const res = await api.get("/v1/connections/catalog");
  if (!res.ok) {
    console.error("Failed to fetch the connections catalog.");
    process.exit(1);
  }
  const catalog = (await res.json()) as Catalog;
  const match = catalog.integrations.find((i) => i.id === id);

  if (!match) {
    console.error(
      `No integration with id "${id}". Run \`clawnify connections list\` for valid ids.\n` +
        `(Provider API keys are set in Settings → API Keys; custom env in Settings → Environment Variables.)`,
    );
    process.exit(1);
  }
  if (match.connected) {
    console.log(`"${match.label}" (${id}) is already connected (● active). Nothing to do.`);
    return;
  }

  const steps = [
    `Open ${INTEGRATIONS_URL}`,
    `Find "${match.label}" and click Connect`,
    `Complete the sign-in / authorization in the browser`,
    `Re-run your command once it shows connected`,
  ];
  if (opts?.json) {
    console.log(
      JSON.stringify(
        { id, label: match.label, connected: false, connect_url: INTEGRATIONS_URL, steps },
        null,
        2,
      ),
    );
    return;
  }
  console.log(`"${match.label}" (${id}) is not connected.\n`);
  console.log(
    "Connecting it needs your authorization in a browser — the CLI and the agent can't do it for you. To connect it:\n",
  );
  steps.forEach((s, n) => console.log(`  ${n + 1}. ${s}`));
  console.log(`\n  ${INTEGRATIONS_URL}`);
}
